import { defineChannelContract } from 'shared/channels';
import type { EntityByIdRequests, EntityStateEvents } from 'shared/channels/entity';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import type { IssueAddress } from './issueAddress';
import type {
  ExportToDocumentationRequest,
  ExportToDocumentationResultData,
  ImportFromDocumentationRequest,
  ImportFromDocumentationResultData,
  RemoveLinkedDocumentationRequest,
  RemoveLinkedDocumentationResultData,
} from './linkedDocumentation/operations';
import type { CreateIssueModel, EditIssueModel, IssueModel } from './issueModel';
import type { FilteredIssues, IssueListAddress, IssuesQuery } from './issueQuery';
import type { IssueArchivalChange } from './issueArchivalChange';
import type { IssueEventAddress, IssueEventDraft, IssueEventEdit } from 'shared/domain/issueEvent/issueEventChange';
import type { IssueEventInDto } from 'shared/dtos/in/issue';
import type { IssueAccessChange, IssueAccessChangeResult } from './issueAccessChange';
import type { ShareableUrlTarget } from './issueShareableUrl';
import type { IncomingEmailMessageContentInDto } from 'shared/dtos/in/issue';
import type { ChangelogOutDto } from 'shared/types/changelogV2';

/**
 * Issue events. A queued change ends with its outcome event carrying the
 * change's `uniqueId`: the saved model (create, edit), or `{ _id }` (remove);
 * a failure is the same event with `error` and `messageId` instead of data.
 */
export type IssueEvents = EntityStateEvents & {
  [DomainMessagesTypes.createdEntity]: IssueModel;
  [DomainMessagesTypes.updatedEntity]: IssueModel;
  [DomainMessagesTypes.deletedEntity]: { _id: string };
  /** Not sent for issues: failures arrive as the outcome event with `error`. */
  [DomainMessagesTypes.failUploadedEntity]: undefined;
  [DomainMessagesTypes.allIssues]: { hasAll?: boolean; items: IssueModel[] };
};

/**
 * Issue requests. Reads wait for the first download of the project's issues;
 * a change is answered once queued and its outcome follows as an event.
 */
export type IssueRequests = EntityByIdRequests<IssueModel> & {
  getFiltered: { request: IssuesQuery; response: FilteredIssues };
  getFilteredIdList: { request: IssuesQuery; response: IssueListAddress[] };
  /** Every issue of the inspection, archived ones included. */
  getOfInspection: { request: { inspectionId: string; timezone: string }; response: IssueModel[] };
  create: { request: { issue: CreateIssueModel; uniqueId: string }; response: void };
  edit: { request: { address: IssueAddress; model: EditIssueModel; uniqueId: string }; response: void };
  remove: { request: IssueArchivalChange; response: void };
  /** Answered with the restored issue (`updatedEntity`), like an edit. */
  restore: { request: IssueArchivalChange; response: void };
  /** Events: answered once the server saved them (not through the HTTP queue), with the saved event. */
  createEvent: { request: IssueEventDraft; response: IssueEventInDto };
  editEvent: { request: IssueEventEdit; response: IssueEventInDto };
  removeEvent: { request: IssueEventAddress; response: void };
  /** Read from the server when asked, not stored. */
  getIncomingEmailMessageContent: {
    request: { issueId: string; contentId: string };
    response: IncomingEmailMessageContentInDto;
  };
  getShareableUrl: { request: ShareableUrlTarget; response: string };
  getChangelog: { request: { issueId: string }; response: ChangelogOutDto };
  /** Answered once the server saved it; a grant of an access the user already has is not an error. */
  changeAccess: { request: IssueAccessChange; response: IssueAccessChangeResult };
  /** Linked documentation: answered once the server saved it (not through the HTTP queue). */
  exportToDocumentation: { request: ExportToDocumentationRequest; response: ExportToDocumentationResultData };
  importFromDocumentation: { request: ImportFromDocumentationRequest; response: ImportFromDocumentationResultData };
  removeLinkedDocumentation: {
    request: RemoveLinkedDocumentationRequest;
    response: RemoveLinkedDocumentationResultData;
  };
  sync: { request: undefined; response: void };
};

/**
 * Not an entity contract: the page never reads all issues (no `getAll`), but
 * the worker still broadcasts the collection event after each download.
 */
export const issueContract = {
  ...defineChannelContract<{ requests: IssueRequests; events: IssueEvents }>('issueChannel'),
  collectionEvent: DomainMessagesTypes.allIssues,
};
